import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Minus, Plus } from "lucide-react";
import { fetchProduct, fetchProducts } from "../api";
import { useSEO } from "../hooks/useSEO";
import { useCart } from "../context/CartContext";
import { NotifyMe } from "../components/NotifyMe";
import { ProductCard } from "../components/ProductCard";
import { Reveal } from "../components/Reveal";
import { money } from "../config/brand";

const Accordion = ({ title, children, testid }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-t border-black/15">
      <button
        data-testid={testid}
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="w-full flex items-center justify-between py-4 text-[11px] font-medium tracking-[0.2em] hover:opacity-50 transition-opacity"
      >
        {title}
        <span className="text-sm leading-none">{open ? "−" : "+"}</span>
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden"
          >
            <div className="pb-5 text-sm leading-relaxed text-black/70">{children}</div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default function ProductPage() {
  const { slug } = useParams();
  const [product, setProduct] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [active, setActive] = useState(0);
  const [size, setSize] = useState(null);
  const [qty, setQty] = useState(1);
  const [related, setRelated] = useState([]);
  const { add } = useCart();

  useSEO(
    product ? `${product.name.toUpperCase()} — RICH KIDS FUNERAL` : "RICH KIDS FUNERAL",
    product?.description || "RICH KIDS FUNERAL. First Collection 26."
  );

  useEffect(() => {
    setProduct(null);
    setNotFound(false);
    setActive(0);
    setSize(null);
    setQty(1);
    fetchProduct(slug)
      .then((p) => {
        setProduct(p);
        fetchProducts({ category: p.category }).then((all) =>
          setRelated(all.filter((x) => x.slug !== p.slug).slice(0, 4))
        );
      })
      .catch(() => setNotFound(true));
  }, [slug]);

  const media = useMemo(() => {
    if (!product) return [];
    const m = (product.images || []).map((src) => ({ type: "img", src }));
    if (product.video) m.push({ type: "video", src: product.video });
    return m;
  }, [product]);

  // Product structured data
  useEffect(() => {
    if (!product) return;
    const el = document.createElement("script");
    el.type = "application/ld+json";
    el.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Product",
      name: product.name,
      image: (product.images || []).map((u) => (u.startsWith("/") ? window.location.origin + u : u)),
      description: product.description,
      sku: product.sku,
      brand: { "@type": "Brand", name: "RICH KIDS FUNERAL" },
      offers: {
        "@type": "Offer",
        price: product.price,
        priceCurrency: "USD",
        availability: product.sold_out
          ? "https://schema.org/SoldOut"
          : "https://schema.org/InStock",
      },
    });
    document.head.appendChild(el);
    return () => el.remove();
  }, [product]);

  if (notFound)
    return (
      <div className="px-4 py-32 text-center" data-testid="product-not-found">
        <p className="text-[11px] tracking-[0.2em] text-black/50">PRODUCT NOT FOUND</p>
        <Link to="/all-products" className="inline-block mt-6 text-[11px] tracking-[0.2em] underline underline-offset-4">
          BACK TO SHOP
        </Link>
      </div>
    );

  if (!product)
    return (
      <div className="px-3 md:px-6 pt-10 grid lg:grid-cols-[1fr_420px] gap-10">
        <div className="aspect-[4/5] bg-[#f5f4f2] animate-pulse" />
        <div className="space-y-4">
          <div className="h-6 w-2/3 bg-[#f5f4f2] animate-pulse" />
          <div className="h-4 w-1/4 bg-[#f5f4f2] animate-pulse" />
        </div>
      </div>
    );

  const soldOut = product.sold_out;
  const comingSoon = product.coming_soon;
  const current = media[active];

  return (
    <div className="px-3 md:px-6 pt-8 md:pt-12" data-testid={`product-page-${product.slug}`}>
      <div className="grid lg:grid-cols-[1fr_420px] gap-10 xl:gap-16">
        {/* GALLERY */}
        <div className="flex gap-4">
          {/* desktop thumbnails */}
          {media.length > 1 && (
            <div className="hidden md:flex flex-col gap-2 w-20 flex-shrink-0">
              {media.map((m, i) => (
                <button
                  key={i}
                  data-testid={`gallery-thumb-${i}`}
                  onClick={() => setActive(i)}
                  className={`aspect-[4/3] overflow-hidden bg-white border transition-colors ${
                    i === active ? "border-black" : "border-transparent hover:border-black/30"
                  }`}
                  aria-label={`View image ${i + 1}`}
                >
                  {m.type === "img" ? (
                    <img src={m.src} alt="" className="w-full h-full object-contain" loading="lazy" />
                  ) : (
                    <span className="w-full h-full flex items-center justify-center text-[9px] tracking-[0.2em] text-black/60">
                      VIDEO
                    </span>
                  )}
                </button>
              ))}
            </div>
          )}
          {/* main media — swipeable on mobile */}
          <div className="flex-1 min-w-0">
            <div
              className="hidden md:block bg-white overflow-hidden"
              data-testid="gallery-main"
            >
              <AnimatePresence mode="wait">
                <motion.div
                  key={active}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  {current?.type === "video" ? (
                    <video
                      src={current.src}
                      autoPlay
                      muted
                      loop
                      playsInline
                      className="w-full max-h-[85vh] object-contain"
                      data-testid="product-video"
                    />
                  ) : (
                    <img
                      src={current?.src}
                      alt={product.name}
                      className="w-full max-h-[85vh] object-contain"
                      data-testid="product-image-main"
                    />
                  )}
                </motion.div>
              </AnimatePresence>
            </div>
            {/* mobile swipe gallery */}
            <div className="md:hidden">
              <div
                className="flex overflow-x-auto snap-x snap-mandatory no-scrollbar bg-white"
                onScroll={(e) => {
                  const i = Math.round(e.target.scrollLeft / e.target.clientWidth);
                  if (i !== active) setActive(i);
                }}
                data-testid="gallery-main-mobile"
              >
                {media.map((m, i) => (
                  <div key={i} className="min-w-full snap-center">
                    {m.type === "video" ? (
                      <video src={m.src} autoPlay muted loop playsInline className="w-full object-contain" />
                    ) : (
                      <img src={m.src} alt={`${product.name} — ${i + 1}`} className="w-full object-contain" loading={i > 0 ? "lazy" : "eager"} />
                    )}
                  </div>
                ))}
              </div>
              {media.length > 1 && (
                <div className="flex justify-center gap-1.5 mt-3">
                  {media.map((_, i) => (
                    <span
                      key={i}
                      className={`w-1.5 h-1.5 rounded-full transition-colors ${
                        i === active ? "bg-black" : "bg-black/20"
                      }`}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* INFO */}
        <div className="lg:sticky lg:top-32 self-start" data-testid="product-info">
          <p className="text-[10px] tracking-[0.25em] text-black/40 uppercase">
            {product.collection}
          </p>
          <h1 className="font-anton-sc text-xl md:text-2xl tracking-[0.1em] mt-2" data-testid="product-name">
            {product.name.toUpperCase()}
          </h1>
          <div className="flex items-baseline gap-3 mt-3">
            <p className="text-sm" data-testid="product-price">{money(product.price)}</p>
            {product.compare_at_price && (
              <p className="text-sm text-black/40 line-through">{money(product.compare_at_price)}</p>
            )}
          </div>
          {soldOut && (
            <p className="text-[11px] font-medium tracking-[0.15em] text-[#e50000] mt-2" data-testid="product-sold-out">
              SOLD OUT
            </p>
          )}
          {!soldOut && comingSoon && (
            <p className="text-[11px] font-medium tracking-[0.15em] text-black/60 mt-2" data-testid="product-coming-soon">
              COMING SOON
            </p>
          )}

          {product.description && (
            <p className="text-sm leading-relaxed text-black/70 mt-5">{product.description}</p>
          )}

          {/* SIZE */}
          {product.sizes?.length > 0 && (
            <div className="mt-7">
              <p className="text-[11px] font-medium tracking-[0.2em] mb-3">SIZE</p>
              <div className="flex flex-wrap gap-2" data-testid="size-selector">
                {product.sizes.map((s) => (
                  <button
                    key={s.label}
                    data-testid={`size-option-${s.label.toLowerCase()}`}
                    disabled={soldOut || (!comingSoon && s.inventory === 0)}
                    onClick={() => setSize(s.label)}
                    className={`min-w-[44px] px-3 py-2.5 text-[11px] tracking-[0.12em] border transition-colors ${
                      size === s.label
                        ? "bg-black text-white border-black"
                        : "border-black/25 hover:border-black"
                    } disabled:line-through disabled:text-black/25 disabled:border-black/10 disabled:cursor-not-allowed`}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* QTY + ADD */}
          <div className="flex gap-3 mt-7">
            <div className="flex items-center border border-black/25">
              <button
                data-testid="qty-minus"
                onClick={() => setQty((q) => Math.max(1, q - 1))}
                className="w-10 h-12 flex items-center justify-center hover:bg-black hover:text-white transition-colors"
                aria-label="Decrease quantity"
              >
                <Minus className="w-3.5 h-3.5" />
              </button>
              <span className="w-10 text-center text-[12px]" data-testid="qty-value">{qty}</span>
              <button
                data-testid="qty-plus"
                onClick={() => setQty((q) => q + 1)}
                className="w-10 h-12 flex items-center justify-center hover:bg-black hover:text-white transition-colors"
                aria-label="Increase quantity"
              >
                <Plus className="w-3.5 h-3.5" />
              </button>
            </div>
            <button
              data-testid="add-to-cart-button"
              disabled={soldOut || comingSoon || (product.sizes?.length > 0 && !size)}
              onClick={() =>
                add({
                  slug: product.slug,
                  name: product.name,
                  price: product.price,
                  image: product.images?.[0],
                  size: size || "",
                  qty,
                })
              }
              className={`flex-1 text-white text-[11px] font-medium tracking-[0.2em] transition-colors disabled:cursor-not-allowed ${
                comingSoon && !soldOut
                  ? "bg-black disabled:bg-black"
                  : "bg-black hover:bg-[#e50000] disabled:bg-black/20"
              }`}
            >
              {soldOut
                ? "SOLD OUT"
                : comingSoon
                ? "COMING SOON"
                : product.sizes?.length > 0 && !size
                ? "SELECT A SIZE"
                : "ADD TO CART"}
            </button>
          </div>

          {comingSoon && !soldOut && <NotifyMe slug={product.slug} size={size} />}

          {/* ACCORDIONS */}
          <div className="mt-9 border-b border-black/15" data-testid="product-accordions">
            {product.details?.length > 0 && (
              <Accordion title="DETAILS" testid="accordion-details">
                <ul className="list-disc pl-4 space-y-1">
                  {product.details.map((d, i) => (
                    <li key={i}>{d}</li>
                  ))}
                </ul>
                {product.made_in && <p className="mt-3">{product.made_in}</p>}
              </Accordion>
            )}
            {product.fit && (
              <Accordion title="FIT" testid="accordion-fit">
                <p>{product.fit}</p>
              </Accordion>
            )}
            {(product.materials || product.care) && (
              <Accordion title="MATERIALS & CARE" testid="accordion-materials">
                {product.materials && <p>{product.materials}</p>}
                {product.care && <p className="mt-1">{product.care}</p>}
              </Accordion>
            )}
            <Accordion title="SHIPPING & RETURNS" testid="accordion-shipping">
              <p>
                Orders are dispatched within 3–5 business days. Worldwide shipping
                available. Free shipping on orders over Rs. 20,000.
              </p>
              <p className="mt-2">
                Returns accepted within 14 days of delivery on unworn items. See{" "}
                <Link to="/returns" className="underline underline-offset-2">
                  returns
                </Link>{" "}
                for details.
              </p>
            </Accordion>
          </div>
        </div>
      </div>

      {/* RELATED */}
      {related.length > 0 && (
        <section className="mt-24" data-testid="related-products">
          <Reveal>
            <h2 className="text-base md:text-lg font-medium tracking-[0.15em]">
              YOU MAY ALSO LIKE
            </h2>
          </Reveal>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-x-3 md:gap-x-6 gap-y-10 mt-8">
            {related.map((p, i) => (
              <Reveal key={p.id} delay={i * 0.05}>
                <ProductCard product={p} />
              </Reveal>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
